import React, { useState } from "react";
import "./PostGenerator.css";
import { useNavigate } from "react-router-dom";
import TurndownService from "turndown";
import { useQuota } from "../../context/QuotaContext";
import PostIcon from "../../assets/PostGenerator/posticon.png";
import SchedulePostModal from "../SchedulePostModal/SchedulePostModal";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import UnderlineExtension from "@tiptap/extension-underline";

import { marked } from "marked";
import DOMPurify from "dompurify";

const PostGenerator = () => {
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [creatorLink, setCreatorLink] = useState("");
  const [prompt, setPrompt] = useState("");
  const [contentType, setContentType] = useState("Insight Posts");
  const [contentCategory, setContentCategory] = useState("Motivational");
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [posted, setPosted] = useState(false); // 👈 posted state
  const [scheduled, setScheduled] = useState(false); // 👈 scheduled state
  const [hasContent, setHasContent] = useState(false); // 👈 track generated/typed content
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { quota, fetchQuota } = useQuota();
  const hasUnlimitedPosts = quota?.plan_limits?.post === -1;
  const hasSchedulingFeature = quota?.plan_features?.post_scheduling === true;

  const editor = useEditor({
    extensions: [StarterKit, UnderlineExtension],
    content: "",
    onUpdate: ({ editor }) => {
      const text = editor.getText().trim();
      setHasContent(!!text); // enable button only if editor has content
    },
  });

  // Compute posts left
  const postsLeft = (() => {
    const limit = quota?.plan_limits?.post ?? 0;
    const used = quota?.usage?.post ?? 0;
    if (limit === -1) return Infinity;
    return Math.max(limit - used, 0);
  })();

  const mapContentType = (frontendType) => {
    switch (frontendType) {
      case "Insight Posts": return "Insight Posts";
      case "Book/Podcast/Event Learnings": return "Learnings";
      case "Personal Story Posts": return "Personal Story";
      case "Educational/How-To Posts": return "Educational/How-To Posts";
      case "Announcement Posts": return "Announcement Posts";
      case "Question/Discussion Posts": return "Question/Discussion Posts";
      case "Case Study Posts": return "Case Study Posts";
      default: return "Insight Posts";
    }
  };

  const mapContentCategory = (frontendCat) => {
    switch (frontendCat) {
      case "Explanation/Analysis": return "Analysis";
      case "List of tips": return "List of Tips";
      case "Striking Advice": return "Striking Advice";
      case "Motivational": return "Motivational";
      case "Automatic": return "Automatic";
      case "Contrarian": return "Contrarian";
      case "Data-Driven": return "Data-Driven";
      case "Humor/Lighthearted": return "Humor/Lighthearted";
      default: return "Motivational";
    }
  };

  const handleGeneratePost = async () => {
    if (!prompt.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/posts/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          prompt,
          content_type: mapContentType(contentType),
          content_category: mapContentCategory(contentCategory),
        }),
      });

      if (!response.ok) throw new Error("Failed to generate post");

      const data = await response.json();
      const rawMarkdown = data.post_text;
      const rawHTML = marked.parse(rawMarkdown);
      const cleanHTML = DOMPurify.sanitize(rawHTML);

      editor?.commands.setContent(cleanHTML, false);

      // Mark content as present
      setHasContent(true);

      // Refresh quota
      await fetchQuota();
    } catch (err) {
      console.error("Post generation failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishPost = async () => {
    if (!editor) return;

    const plainText = editor.getText().trim();
    if (!plainText) return;

    setPublishing(true);

    try {
      const htmlContent = editor.getHTML();
      const turndownService = new TurndownService();
      const markdownText = turndownService.turndown(htmlContent);

      const response = await fetch(`${API_URL}/api/posts/publish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ post_text: markdownText }),
      });

      if (!response.ok) throw new Error("Failed to publish");

      const result = await response.json();
      console.log("Publish response:", result);

      // ✅ Mark as posted
      setPosted(true);
    } catch (err) {
      console.error("Failed to publish post:", err);
    } finally {
      setPublishing(false);
    }
  };

  const handleScheduleClick = () => {
    if (!hasSchedulingFeature) {
      // Redirect to pricing if user doesn't have scheduling feature
      navigate("/pricing");
      return;
    }
    setShowScheduleModal(true);
  };

  const handleScheduled = (result) => {
    console.log("Post scheduled:", result);
    setScheduled(true);
    // Optionally clear the editor or show success message
  };

  return (
    <div className="post-generator-wrapper">
      {!hasUnlimitedPosts && (
        <button
          className="post-limit-badge"
          onClick={() => navigate("/pricing")}
        >
          Get Unlimited Access
        </button>
      )}

      <div className="post-generator-container">
        {/* Sidebar */}
        <div className="post-generator-sidebar card-style">
          <textarea
            className="post-description"
            placeholder="Describe your post here."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          ></textarea>

          <div className="input-group coming-soon-group">
            <label className="input-label">Writing Style</label>
            <div className="add-creator-wrapper">
              <button className="add-creator-btn disabled" disabled>
                + Add a creator
              </button>
              <span className="coming-soon-label">Coming Soon</span>
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">Content Type</label>
            <select
              className="dropdown"
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
            >
              <option>Insight Posts</option>
              <option>Book/Podcast/Event Learnings</option>
              <option>Personal Story Posts</option>
              <option>Educational/How-To Posts</option>
              <option>Announcement Posts</option>
              <option>Question/Discussion Posts</option>
              <option>Case Study Posts</option>
            </select>
          </div>

          <div className="input-group">
            <label className="input-label">Content Category</label>
            <select
              className="dropdown"
              value={contentCategory}
              onChange={(e) => setContentCategory(e.target.value)}
            >
              <option>Motivational</option>
              <option>Automatic</option>
              <option>Striking Advice</option>
              <option>Explanation/Analysis</option>
              <option>List of tips</option>
              <option>Contrarian</option>
              <option>Data-Driven</option>
              <option>Humor/Lighthearted</option>
            </select>
          </div>

          <button
            className="generate-btn"
            onClick={handleGeneratePost}
            disabled={loading || postsLeft === 0}
          >
            {loading ? "Generating..." : "Generate Post"}
          </button>

          {postsLeft !== Infinity && postsLeft <= 0 && (
            <p className="no-posts-text">
              You’ve used all your posts. Upgrade for more.
            </p>
          )}
        </div>

        {/* Editor */}
        <div className="post-generator-editor card-style">
          {editor ? (
            <EditorContent editor={editor} className="tiptap-editor" />
          ) : (
            <div className="tiptap-editor">Loading editor...</div>
          )}

          <div className="editor-footer">
            <button
              className={`schedule-btn ${hasContent && !posted && !scheduled ? "active" : "disabled"
                }`}
              onClick={handleScheduleClick}
              disabled={!hasContent || posted || scheduled}
              title={!hasSchedulingFeature ? "Upgrade to schedule posts" : "Schedule this post"}
            >
              {scheduled ? "Scheduled!" : "Schedule Post"}
            </button>
            <button
              className={`linkedin-btn ${hasContent && !posted && !scheduled ? "active" : "disabled"
                }`}
              onClick={handlePublishPost}
              disabled={!hasContent || publishing || posted || scheduled}
            >
              {publishing
                ? "Publishing..."
                : posted
                  ? "Posted!"
                  : "Post on LinkedIn"}{" "}
              <img src={PostIcon} alt="Post" className="post-icon" />
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreatorModal && (
        <div
          className="creator-modal-overlay"
          onClick={() => setShowCreatorModal(false)}
        >
          <div className="creator-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="creator-modal-title">Customize Your Posts</h3>
            <p className="creator-modal-text">
              Simply paste link of your preferred LinkedIn creator & we'll
              import their content style
            </p>
            <input
              className="creator-input"
              type="text"
              value={creatorLink}
              onChange={(e) => setCreatorLink(e.target.value)}
              placeholder="https://linkedin.com/u/your-creator"
            />
            <button
              className="creator-done-btn"
              onClick={() => setShowCreatorModal(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}

      <SchedulePostModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        postContent={editor ? editor.getText().trim() : ""}
        onScheduled={handleScheduled}
      />
    </div>
  );
};

export default PostGenerator;
